import dayjs from 'dayjs'
import { Text } from '$components'
import {
  Button,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateValue,
  Heading,
  RangeCalendar,
} from 'react-aria-components'
import { ChevronLeftIcon } from '$components'
import { parseDate, CalendarDate, today, getLocalTimeZone } from '@internationalized/date'
import { cn } from '$components'
import { useState, useMemo } from 'react'
import { useFilters } from '../hooks/use-filters'
import { DateRangeFilterDefinition } from '../types'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export type DateRangeFilterOptionProps = {
  filter: DateRangeFilterDefinition
}

export function DateRangeFilterOption({ filter }: DateRangeFilterOptionProps) {
  const { setFilter, getFilter } = useFilters()
  const currentValue = getFilter(filter)

  const selectedRange = useMemo(() => {
    if (!currentValue) {
      return null
    }

    try {
      return {
        start: parseDate(dayjs.utc(currentValue.start).format('YYYY-MM-DD')),
        end: parseDate(dayjs.utc(currentValue.end).format('YYYY-MM-DD')),
      }
    } catch {
      return null
    }
  }, [currentValue])

  const [visibleDate, setVisibleDate] = useState<CalendarDate>(
    selectedRange?.start
      ? new CalendarDate(selectedRange.start.year, selectedRange.start.month, selectedRange.start.day)
      : today(getLocalTimeZone()),
  )

  const handleRangeChange = (range: { start: DateValue; end: DateValue } | null) => {
    if (range?.start && range?.end) {
      const startDate = dayjs.utc(range.start.toString())
      const endDate = dayjs.utc(range.end.toString())
      const rangeValue = {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      }
      setFilter(filter, rangeValue)
    } else {
      setFilter(filter, null)
    }
  }

  const handleMonthChange = (month: number) => setVisibleDate((prev: CalendarDate) => new CalendarDate(prev.year, month, 1))
  const handleYearChange = (year: number) => setVisibleDate((prev: CalendarDate) => new CalendarDate(year, prev.month, 1))

  return (
    <RangeCalendar
      aria-label={filter.label}
      value={selectedRange as unknown as { start: DateValue; end: DateValue }}
      onChange={handleRangeChange}
      focusedValue={visibleDate as unknown as DateValue}
      onFocusChange={(date) => {
        if (date && typeof date.year === 'number' && typeof date.month === 'number' && typeof date.day === 'number') {
          setVisibleDate(new CalendarDate(date.year, date.month, date.day))
        }
      }}
    >
      <header className="flex items-center justify-start sm:justify-center gap-2 border-b border-gray-100 px-4 pt-2 pb-4 sm:py-2">
        <Button
          slot="previous"
          className="p-2 hover:bg-gray-50 rounded-lg text-gray-600"
          onPress={() => setVisibleDate((prev: CalendarDate) => prev.subtract({ months: 1 }))}
        >
          <ChevronLeftIcon className="size-4" />
        </Button>

        <div className="flex items-center gap-2">
          <Heading className="sr-only" />
          <MonthPicker
            value={visibleDate.month}
            onChange={handleMonthChange}
          />
          <YearPicker
            value={visibleDate.year}
            onChange={handleYearChange}
          />
        </div>

        <Button
          slot="next"
          className="p-2 hover:bg-gray-50 rounded-lg text-gray-600"
          onPress={() => setVisibleDate((prev: CalendarDate) => prev.add({ months: 1 }))}
        >
          <ChevronLeftIcon className="rotate-180 size-4" />
        </Button>
      </header>

      <div className={cn({
        'p-4': !selectedRange,
        'pt-8 sm:pt-4 px-4 pb-2': selectedRange,
      })}
      >
        <CalendarGrid className="w-full">
          <CalendarGridHeader>
            {(day) => (
              <CalendarHeaderCell className="text-xs text-gray-500 font-semibold pb-2 text-center">
                {day}
              </CalendarHeaderCell>
            )}
          </CalendarGridHeader>
          <CalendarGridBody>
            {(date) => (
              <CalendarCell
                date={date}
                className={cn(
                  'size-8 outline-none cursor-default rounded-full flex items-center justify-center text-sm text-gray-700',
                  'hover:bg-gray-50 focus-visible:border-2 border-indigo-400 mx-auto',
                  'data-[selected]:bg-gray-900 data-[selected]:text-white',
                  'data-[selection-start]:bg-gray-900 data-[selection-start]:text-white',
                  'data-[selection-end]:bg-gray-900 data-[selection-end]:text-white',
                  'data-[hovered]:bg-gray-100',
                  'data-[pressed]:bg-gray-100',
                  'data-[outside-month]:text-gray-500',
                  'data-[disabled]:!text-gray-400 data-[disabled]:hover:!bg-transparent',
                  'data-[range-selection]:bg-gray-100',
                )}
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>

        { selectedRange && (
          <div className="flex w-full pt-2 justify-end">
            <button
              className="w-fit px-4 py-2 sm:px-2 sm:py-1.5 focus:outline-none hover:bg-gray-50/50 rounded-lg"
              onClick={() => handleRangeChange(null)}
            >
              <Text type="s">Clear selection</Text>
            </button>
          </div>
        )}
      </div>
    </RangeCalendar>
  )
}

type MonthPickerProps = {
  value: number
  onChange: (month: number) => void
}

function MonthPicker({ value, onChange }: MonthPickerProps) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]

  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-sm font-medium text-gray-700 bg-transparent border-none outline-none cursor-pointer"
    >
      {months.map((month, index) => (
        <option
          key={month}
          value={index + 1}
        >
          {month}
        </option>
      ))}
    </select>
  )
}

type YearPickerProps = {
  value: number
  onChange: (year: number) => void
}

function YearPicker({ value, onChange }: YearPickerProps) {
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i)

  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-sm font-medium text-gray-700 bg-transparent border-none outline-none cursor-pointer"
    >
      {years.map((year) => (
        <option
          key={year}
          value={year}
        >
          {year}
        </option>
      ))}
    </select>
  )
}
